import { useCallback, useEffect, useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { TerminalShell } from "@/components/TerminalShell";
import { paymentService } from "@/services/paymentService";
import { tradingService } from "@/services/marketService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate, fmtMoney } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";

const input = "h-11 w-full rounded-md border border-[#1D2637] bg-[#0E1523] px-3 text-sm text-white outline-none focus:border-[#2563EB]";
const STATUS = { pending: "text-[#F59E0B]", approved: "text-[#10B981]", rejected: "text-[#EF4444]" };

export default function DepositPage() {
  const { t } = useLanguage();
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState(null);
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, h, s] = await Promise.all([
        paymentService.methods(), paymentService.deposits(), tradingService.summary(),
      ]);
      setMethods(m);
      setHistory(h);
      setSummary(s);
      setMethod((prev) => prev || m[0] || null);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await paymentService.createDeposit({ method_id: method.id, amount: Number(amount), reference });
      toast.success(t("deposit.registered"));
      setAmount(""); setReference("");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TerminalShell account={summary}>
      <div data-testid="deposit-page" className="grid grid-cols-1 gap-6 p-4 sm:p-8 xl:grid-cols-2">
        <section className="space-y-5 rounded-md border border-[#141A26] bg-[#0A0E16] p-6">
          <h1 className="font-heading text-2xl font-light tracking-tight text-white">{t("deposit.title")}</h1>
          {methods.length === 0 ? (
            <p className="text-sm text-[#8A97AD]">{t("deposit.none")}</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {methods.map((m) => (
                  <button key={m.id} onClick={() => setMethod(m)} data-testid={`method-${m.type}-${m.id}`}
                          className={`rounded-md border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${
                            method?.id === m.id ? "border-[#2563EB] text-white" : "border-[#1D2637] text-[#8A97AD]"}`}>
                    {m.label}
                  </button>
                ))}
              </div>

              {method ? (
                <div className="space-y-4 rounded-md border border-[#1D2637] bg-[#0E1523] p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A97AD]">
                    {method.type === "pix" ? t("deposit.pixKey") : `${t("deposit.wallet")} ${method.type.toUpperCase()}`}
                    {method.network ? ` · ${method.network}` : ""}
                  </p>
                  <div className="flex items-center gap-3">
                    <p className="min-w-0 flex-1 break-all font-mono text-sm text-white" data-testid="method-address">
                      {method.address}
                    </p>
                    <button onClick={() => { navigator.clipboard?.writeText(method.address); toast.success(t("deposit.copied")); }}
                            data-testid="copy-address" className="rounded-md border border-[#1D2637] p-2 text-[#8A97AD] hover:text-white">
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                  <img alt="QR" data-testid="method-qr" className="h-40 w-40 rounded bg-white p-2"
                       src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(method.address)}`} />
                  {method.instructions ? <p className="text-xs text-[#8A97AD]">{method.instructions}</p> : null}
                </div>
              ) : null}

              <form onSubmit={submit} className="space-y-3" data-testid="deposit-form">
                <input type="number" min="0" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)}
                       placeholder={t("deposit.amount")} data-testid="deposit-amount-input" className={`${input} font-mono`} />
                <input value={reference} onChange={(e) => setReference(e.target.value)}
                       placeholder={t("deposit.reference")} data-testid="deposit-reference-input" className={input} />
                <button type="submit" disabled={busy || !method} data-testid="deposit-submit"
                        className="h-11 w-full rounded-md bg-[#2563EB] text-xs font-semibold uppercase tracking-[0.15em] text-white hover:bg-[#1D4ED8] disabled:opacity-50">
                  {busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : t("deposit.confirm")}
                </button>
              </form>
            </>
          )}
        </section>

        <section className="rounded-md border border-[#141A26] bg-[#0A0E16] p-6">
          <h2 className="font-heading text-lg font-light tracking-tight text-white">{t("deposit.history")}</h2>
          <div className="mt-4 space-y-2" data-testid="deposit-history">
            {history.map((d) => (
              <div key={d.id} data-testid={`deposit-row-${d.id}`}
                   className="flex items-center justify-between border-b border-[#101724] py-3 text-sm">
                <div>
                  <p className="font-mono text-white">{fmtMoney(d.amount)}</p>
                  <p className="text-xs text-[#8A97AD]">{d.method_label} · {fmtDate(d.created_at)}</p>
                </div>
                <span className={`font-mono text-xs uppercase ${STATUS[d.status]}`}>{t(`status.${d.status}`)}</span>
              </div>
            ))}
            {history.length === 0 ? <p className="py-8 text-center text-sm text-[#5C6B82]">{t("deposit.empty")}</p> : null}
          </div>
        </section>
      </div>
    </TerminalShell>
  );
}
